import { Component, OnDestroy, inject } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { Store } from '@ngrx/store';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';
import { LocalStorageService } from 'app/services/localStorage.service';
import { AppState } from 'app/types';

export enum DebuggerType {
  FEATURE_FLAG = 'feature flag',
  MEDIA = 'media',
  NETWORK = 'network',
  PEER_CONNECTION = 'peer connections',
  SCREENSHARE = 'screenshare',
  PERFORMANCE = 'performance',
  ROOM_SETTINGS = 'room settings',
  SCENE = 'scene',
  SOUND = 'sound',
  SUBSCRIPTION = 'subscription',
  TEST_UTILITIES = 'test utilities',
  SPECIAL_ACCESS_GROUP = 'special access group',
}
export const DEBUGGER_OPTION = 'ScootDebuggerOption';

@Component({
  selector: 'app-debugger',
  templateUrl: './debugger.component.html',
  styleUrls: ['./debugger.component.scss'],
  standalone: false,
})
export class DebuggerComponent implements OnDestroy {
  private localStorageService = inject(LocalStorageService);

  public showDebugger = false;

  public DebuggerType = DebuggerType;
  public selectedDebugger = DebuggerType.TEST_UTILITIES;

  private destroyed$ = new ReplaySubject<boolean>(1);
  protected min = false;

  public constructor() {
    const store = inject<Store<AppState>>(Store);

    getFeatureFlagObservable(store, LaunchDarklyFlags.social_debugger)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(this.handleAllowDebugger.bind(this));

    const debuggerOption = this.localStorageService.retrieve(
      DEBUGGER_OPTION,
      (option) => JSON.parse(option) as DebuggerType,
    );
    if (debuggerOption) {
      this.selectedDebugger = debuggerOption;
    }
  }

  public ngOnDestroy(): void {
    this.removeEventListener();
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private handleAllowDebugger(allowDebugger: boolean) {
    if (allowDebugger) {
      this.addEventListener();
    } else {
      this.removeEventListener();
      this.showDebugger = false;
    }
  }

  public onDebuggerChange({ value }: MatSelectChange): void {
    this.selectedDebugger = value;
    this.localStorageService.store(DEBUGGER_OPTION, this.selectedDebugger);
  }

  private addEventListener() {
    window?.addEventListener('keydown', this.keyDownFn);
  }

  private removeEventListener() {
    window?.removeEventListener('keydown', this.keyDownFn);
  }

  private keyDownFn = (e: KeyboardEvent): void => {
    if (e.shiftKey && e.ctrlKey && e.key === 'D') {
      this.showDebugger = !this.showDebugger;
    }
  };
}
